import { useDroppable } from "@dnd-kit/core";
import { EChatListKind, ESidebarView } from "@frontend-shared/types";
import {
  formatChatDateGroupLabel,
  groupChatsByAssistant,
  groupChatsByDate,
  selectPinnedChats,
} from "@frontend-shared/utils";
import { Pin } from "lucide-react";
import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAssistants } from "@/api/assistants";
import { useChatList } from "@/api/chat/useChatList";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCollapsedSidebarSections } from "@/hooks/use-collapsed-sidebar-sections";

import { useSidebarInfiniteScroll } from "../hooks/use-sidebar-infinite-scroll";
import { AssistantChatGroups } from "./components/AssistantChatGroups";
import { ChatListItem } from "./components/ChatListItem";
import { ChatListSkeleton } from "./components/ChatListSkeleton";
import { SidebarGroupCount } from "./components/SidebarGroupCount";

const PINNED_GROUP_KEY = "pinned" as const;

interface IChatListProps {
  view: ESidebarView;
}

const ChatListInner = ({ view }: IChatListProps): JSX.Element => {
  const { t, i18n } = useTranslation("sidebar");
  const isAssistantView = view === ESidebarView.ASSISTANT_CHATS;

  const { ref, isInView } = useSidebarInfiniteScroll();
  const { setNodeRef: setRootDropRef, isOver: isRootDropOver } = useDroppable({
    id: "root-chat-list",
    data: { type: "root" },
  });

  // NOTE: NSAI-5678 — two lists, cached and paginated apart: regular chats
  // feed the date buckets, assistant chats the "Assistant chats" tab. Splitting
  // on the server is what makes an old assistant chat reachable from its
  // section without paging the regular list all the way down to it.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useChatList({ kind: EChatListKind.REGULAR });
  const {
    data: assistantData,
    isLoading: isAssistantListLoading,
    fetchNextPage: fetchNextAssistantPage,
    hasNextPage: hasNextAssistantPage,
    isFetchingNextPage: isFetchingNextAssistantPage,
  } = useChatList({ kind: EChatListKind.ASSISTANT });

  useEffect(() => {
    // NOTE: NSAI-6342 — the sentinel below only exists in the chats tab, so
    // paging the regular list is the chats tab's business; the assistant tab
    // pages its own list from inside AssistantChatGroups.
    if (!isAssistantView && isInView && hasNextPage) {
      fetchNextPage();
    }
  }, [isAssistantView, isInView, hasNextPage, fetchNextPage]);

  const regularChats = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const assistantChats = useMemo(
    () => assistantData?.pages.flatMap((page) => page.data) ?? [],
    [assistantData],
  );

  // NOTE: NSAI-6342 — pinned chats follow their own tab rather than sharing one
  // section across both lists, the way mobile splits them (NSAI-5622). One
  // merged "Pinned" section would put an assistant chat at the top of the tab
  // that exists to exclude assistant chats, and would leave a pinned assistant
  // chat missing from the tab a member went looking for it in.
  const pinnedChats = useMemo(
    () =>
      selectPinnedChats({
        chats: isAssistantView ? assistantChats : regularChats,
      }),
    [isAssistantView, assistantChats, regularChats],
  );

  const assistantGroups = useMemo(
    () =>
      groupChatsByAssistant({
        chats: assistantChats.filter(
          (chat) => !chat.isPinned && !chat.folderId,
        ),
      }),
    [assistantChats],
  );

  // NOTE: NSAI-5678 (review follow-up) — gated on there being a group to
  // render, not fetched unconditionally on every sidebar mount (every route).
  // The list payload carries no avatars (data URLs of up to 512KB); this is
  // purely how the section resolves them for the assistants it's about to
  // render. Ungated, this raced Chat.tsx's own useAssistants() call on the
  // shared [ASSISTANTS_QUERY_KEY, ALL] query: React Query's default
  // refetch-on-mount fired a second, unnecessary request on every chat-page
  // load, occasionally landing between Chat.tsx reading a cached "assistant
  // present" answer and the deleted-assistant unavailability check re-reading
  // it — surfacing that banner a beat earlier than the websocket error which
  // is supposed to trigger it.
  const { data: assistants } = useAssistants({
    enabled: isAssistantView && assistantGroups.length > 0,
  });

  const assistantAvatarByUuid = useMemo(
    () =>
      Object.fromEntries(
        (assistants ?? []).map((assistant) => [
          assistant.uuid,
          assistant.avatar,
        ]),
      ),
    [assistants],
  );

  // NOTE: Root-level unpinned regular chats bucketed into Today / Yesterday /
  // Previous 7 days / Previous 30 days / month / year by last activity
  // (NSAI-4559). The shared helper keeps web and mobile in step.
  const dateGroups = useMemo(
    () =>
      groupChatsByDate({
        chats: regularChats.filter((chat) => !chat.isPinned && !chat.folderId),
      }),
    [regularChats],
  );

  // NOTE: Month and year buckets have data-dependent keys, so the accordion is
  // controlled: every known section is open unless the user collapsed it. A
  // static defaultValue cannot work here — buckets that appear only after the
  // next page loads would render collapsed.
  // NOTE: The pinned key only joins the set while its section is actually
  // rendered. Radix derives the next open set from the `value` we pass and only
  // adds/removes the toggled item, so an unrendered key would survive untouched
  // today — but keying off what is on screen means this cannot start silently
  // marking it collapsed if that internal ever changes.
  const groupKeys = useMemo(
    () => [
      ...(pinnedChats.length > 0 ? [PINNED_GROUP_KEY] : []),
      ...(isAssistantView ? [] : dateGroups.map((group) => group.key)),
    ],
    [pinnedChats.length, isAssistantView, dateGroups],
  );

  // NOTE: NSAI-5926 — which of these the user folded away is remembered in
  // localStorage, so a collapsed section stays collapsed across reloads and
  // tabs instead of springing back open on every visit.
  const { openSectionKeys, handleOpenSectionsChange } =
    useCollapsedSidebarSections({ sectionKeys: groupKeys });

  // NOTE: NSAI-6342 — each tab waits on its own list. Waiting on both would
  // hold the chats tab behind a request it does not render a single row from.
  if (isAssistantView ? isAssistantListLoading : isLoading) {
    return <ChatListSkeleton />;
  }

  const hasVisibleChats = isAssistantView
    ? pinnedChats.length > 0 || assistantGroups.length > 0
    : pinnedChats.length > 0 || dateGroups.length > 0;

  return (
    <div
      ref={setRootDropRef}
      data-testid="root-chat-list-drop-zone"
      data-sidebar-view={view}
      className={`space-y-1 ${isRootDropOver ? "ring-2 ring-primary/50 rounded-md" : ""}`}
    >
      <Accordion
        type="multiple"
        value={openSectionKeys}
        onValueChange={handleOpenSectionsChange}
        className="w-full"
      >
        {pinnedChats.length > 0 && (
          <AccordionItem value={PINNED_GROUP_KEY} className="border-b-0">
            <AccordionTrigger className="text-[15px] font-normal text-foreground px-3 py-1 hover:no-underline justify-start gap-x-1">
              <Pin className="h-3 w-3" />
              {t("chat_folder_list.pinned")}
              {/* NOTE: NSAI-6259 follow-up — pinned chats are picked out of
                  what this tab's list has loaded, so a chat pinned weeks ago
                  that no page has reached yet is not counted. Partial while
                  that list has more to fetch. */}
              <SidebarGroupCount
                count={pinnedChats.length}
                isPartial={isAssistantView ? hasNextAssistantPage : hasNextPage}
              />
            </AccordionTrigger>
            <AccordionContent className="space-y-0.5">
              {pinnedChats.map((chat) => (
                <ChatListItem key={chat.uuid} chat={chat} />
              ))}
            </AccordionContent>
          </AccordionItem>
        )}
        {/* NOTE: NSAI-6342 — the assistant groups are the whole tab now, so
            they render without the "Assistant chats" accordion that used to
            wrap them. That wrapper's own count was the inconsistency this
            ticket is about: a total over every assistant sat where each date
            bucket carries a count of the rows directly under it, so the same
            numeral meant two different things one section apart. The tab is
            the heading, and the only counts left are the per-group ones. */}
        {isAssistantView && assistantGroups.length > 0 && (
          <AssistantChatGroups
            groups={assistantGroups}
            assistantAvatarByUuid={assistantAvatarByUuid}
            hasNextPage={hasNextAssistantPage}
            isFetchingNextPage={isFetchingNextAssistantPage}
            fetchNextPage={fetchNextAssistantPage}
          />
        )}
        {!isAssistantView &&
          dateGroups.map((group, index) => (
            <AccordionItem
              key={group.key}
              value={group.key}
              className="border-b-0"
            >
              <AccordionTrigger className="text-[15px] font-normal text-foreground px-3 py-1 hover:no-underline justify-start gap-x-1">
                {formatChatDateGroupLabel({
                  group,
                  locale: i18n.language,
                  translate: t,
                })}
                {/* NOTE: NSAI-6259 follow-up — the count is of the chats
                    LOADED, and the list pages as the member scrolls. The
                    buckets are date-ordered, so only the LAST one can still
                    grow: an older page cannot add a chat to "Today" once
                    "Yesterday" has started. Marking just that bucket partial
                    keeps every complete count exact and stops the last one
                    claiming a total it does not have. */}
                <SidebarGroupCount
                  count={group.chats.length}
                  isPartial={hasNextPage && index === dateGroups.length - 1}
                />
              </AccordionTrigger>
              <AccordionContent className="space-y-0.5">
                {group.chats.map((chat) => (
                  <ChatListItem key={chat.uuid} chat={chat} />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        {!hasVisibleChats && (
          // NOTE: NSAI-4345 — the design draws the empty history as two plain
          // lines of secondary text, not a dashed placeholder box.
          // NOTE: NSAI-6342 — each tab says what IT is missing: "no chats yet"
          // under a tab that only ever shows assistant chats would read as a
          // claim about the whole history.
          <p
            data-testid="sidebar-chat-list-empty-state"
            className="px-3 py-1 text-sm text-balance text-foreground-secondary"
          >
            {isAssistantView
              ? t("view_switch.empty_assistant_chats")
              : t("chat_list.empty_state")}
          </p>
        )}
      </Accordion>
      {!isAssistantView && (
        <div ref={ref}>{isFetchingNextPage && <ChatListSkeleton />}</div>
      )}
    </div>
  );
};

export const ChatList = memo(ChatListInner);
